
import React, { useState } from "react";
import {
    Box,
    Typography,
    Toolbar,
    InputAdornment,
    IconButton,
    Paper,
    Tooltip,
    Stack,
    Checkbox,
    TextField
} from "@mui/material";
import {ContainerClasses} from '../../themes/containerClasses'
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import MenuIcon from "@mui/icons-material/Menu";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import AccountCircle from "@mui/icons-material/AccountCircle";
import {useTheme} from '@mui/material/styles';
import {LayoutFieldModel, RecordModel} from '../../models/models';
import {checkLayoutFieldRequirement} from '../../utils/fieldValidations';
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DOMPurify from "dompurify"


export const DisplayFieldComponent: React.FC<{
  layoutField: LayoutFieldModel,
  record: RecordModel,
}> = ( {
  layoutField,
  record
}) => {

  const theme = useTheme();
  const styles = ContainerClasses();

  const [preview, setPreview] = useState<boolean>(true)

  const getValue = () => {

    const fieldApiName = layoutField.ReferencedField_sys.ApiName_sys;
    const recordField = record.fields[fieldApiName];

    if(layoutField.ReferencedField_sys.Type_sys === 'ForeignKey') return recordField?.fieldValue?.lookup_display_value;
    if(layoutField.ReferencedField_sys.Type_sys === 'OneToOneField') return recordField?.fieldValue?.lookup_display_value;
    if(layoutField.ReferencedField_sys.Type_sys === 'JSONField'){
      if(typeof(recordField?.fieldValue) === 'string') return recordField?.fieldValue
      return JSON.stringify(recordField?.fieldValue, null, 2)
    }

    return recordField?.fieldValue

  }


  const labelElement = (layoutField: LayoutFieldModel) => {
    const helperText = layoutField.ReferencedField_sys.HelpText_sys

    const ThisLabel = (
        <Typography
          variant="body1"
          sx={{color: 'text.secondary',
               fontWeight: 600,
               minWidth: '20%',
               textAlign: 'left'}}
        >
          {layoutField.ReferencedField_sys.Label_sys}:
        </Typography>
    )

    if(helperText){
      return (
        <Tooltip title={helperText}>{ThisLabel}</Tooltip>
      )
    }
    return (ThisLabel)
  }


  const booleanFieldElement = () => {
    const value = getValue();

    return (
      <Paper
          sx= {{
            p: 2,
            overflowY: "auto",
            borderRadius: 3,
            textAlign: 'left'
          }}
          variant="outlined"
      >
        {labelElement(layoutField)}

        <Typography
          variant="body1"
          sx={{textAlign: 'left'}}
        >
        {value === true ?
          <CheckBoxIcon/>
          :
          <CheckBoxOutlineBlankIcon/>
        }
        </Typography>
      </Paper>
    )
  }

  const textFieldElement = () => {
    const value = getValue();

    return (
        <Paper
          sx= {{
            p: 2,
            overflowY: "auto",
            borderRadius: 3,
            textAlign: 'left'
          }}
          variant="outlined"
        >
          {labelElement(layoutField)}

          <Box
            sx={{textAlign: 'left'}}
          >
          <Typography
            variant="body1"
            sx={{color: 'text.primary',
                 fontWeight: 500,
                 textAlign: 'left'}}
          >
           {value}
          </Typography>
        </Box>
      </Paper>
    )
  }

  const jsonFieldElement = () => {
    const value = getValue()
    return (
      <Paper
        sx= {{
          p: 2,
          minHeight: 120,
          maxHeight: 247,
          overflowY: "auto",
          borderRadius: 3,
          textAlign: 'left',
          color: theme.palette.text.primary
        }}
        variant="outlined"
      >
        {labelElement(layoutField)}

        <Typography
          component='pre'
          sx={{
            whiteSpace: 'pre-wrap',
            color: 'text.primary',
            fontWeihgt: 500,
            textAlign: 'left'
          }}
        >
         {value}
        </Typography>
      </Paper>
    )
  }



  const htmlFieldElement = () => {

    const value = getValue();

    return (
      <Box position="relative">
        <Paper
          sx= {{
            p: 2,
            minHeight: 120,
            maxHeight: 247,
            overflowY: "auto",
            borderRadius: 3,
            textAlign: 'left',
            color: theme.palette.text.primary
          }}
          variant="outlined"
        >
          {labelElement(layoutField)}

        {preview &&
          <Box
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(value)}}
          />}

        {!preview &&
          <Typography
            component='pre'
            sx={{
              whiteSpace: 'pre-wrap',
              color: 'text.primary',
              fontWeihgt: 500,
              textAlign: 'left'
            }}
          >
            {value}
          </Typography>}

          <Tooltip title={preview ? 'View Source' : 'Preview HTML'} arrow>
            <IconButton
              size="small"
              onClick={() => setPreview(!preview)}
              sx={{
                position: "absolute",
                top: 8,
                right: 8
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Paper>
      </Box>
  )
}





  if(layoutField.ReferencedField_sys.Type_sys ==='CharField' &&
     layoutField.ReferencedField_sys.PicklistValues_sys === null) return textFieldElement()
  else if(layoutField.ReferencedField_sys.Type_sys === 'TextField' &&
          layoutField.ReferencedField_sys.PicklistValues_sys === null) return textFieldElement()
  else if(layoutField.ReferencedField_sys.Type_sys === 'CharField') return textFieldElement()
  else if(layoutField.ReferencedField_sys.Type_sys === 'HtmlField' &&
          layoutField.ReferencedField_sys.PicklistValues_sys === null) return htmlFieldElement()
  else if(layoutField.ReferencedField_sys.Type_sys === 'BooleanField') return booleanFieldElement()
  else if(layoutField.ReferencedField_sys.Type_sys === 'DateField') return textFieldElement()
  else if(layoutField.ReferencedField_sys.Type_sys === 'DateTimeField') return textFieldElement()
  else if(layoutField.ReferencedField_sys.Type_sys === 'JSONField') return jsonFieldElement()
  else if(layoutField.ReferencedField_sys.Type_sys === 'EmailField') return textFieldElement()
  else if(layoutField.ReferencedField_sys.Type_sys === 'PhoneField') return textFieldElement()
  else if(layoutField.ReferencedField_sys.Type_sys === 'IntegerField') return textFieldElement()
  else if(layoutField.ReferencedField_sys.Type_sys === 'ForeignKey') return textFieldElement()
  else if(layoutField.ReferencedField_sys.Type_sys === 'OneToOneField') return textFieldElement()
  return (<Box key={layoutField.Uuid_sys}>TODO: {layoutField.ReferencedField_sys.Type_sys} </Box>)

/*




                          else if(layout_field.ReferencedField_sys.Type_sys === 'URLField') return urlFieldElement(layoutField, record)
                          else if(layout_field.ReferencedField_sys.Type_sys === 'DecimalField') return decimalFieldElement(layoutField, record)

                          else if(layout_field.ReferencedField_sys.Type_sys === 'ForeignKey') return lookupFieldElement(layoutField, record)
                          else if(layout_field.ReferencedField_sys.Type_sys === 'EncryptedCharField') return encryptedCharFieldElement(layoutField, record)
                          else if(layout_field.ReferencedField_sys.Type_sys === 'BigAutoField') return bigAutoFieldElement(layoutField, record)
                          else if(layout_field.ReferencedField_sys.Type_sys === 'UUIDField') return uuidFieldElement(layoutField, record)
                          else if(layout_field.ReferencedField_sys.Type_sys === 'AutoIdentifierNameField') return autoIdentifierNameFieldElement(layoutField, record)
                          else if(layout_field.ReferencedField_sys.Type_sys === 'FileField') return fileFieldElement(layoutField, record)
                          else if(layout_field.ReferencedField_sys.Type_sys === 'FormulaField') return formulaFieldElement(layoutField, record)
                          else if(layout_field.ReferencedField_sys.Type_sys === 'ProcedureField') return procedureFieldElement(layoutField, record)

                          return (<Box key={layout_field.Uuid_sys}>TODO: {layout_field.ReferencedField_sys.Type_sys} </Box>)
*/

};
